/**
 * Rupee formatting.
 *
 * Money arrives from the API as a decimal string, never a number, so that a
 * rupee amount does not round-trip through a float on its way to the screen.
 * This formats the string directly and never parses it.
 *
 * Mirrors format_inr in backend/app/core/money.py, including Indian digit
 * grouping — last three digits, then pairs, so 1234567.89 reads 12,34,567.89.
 */
export function money(value: string | null | undefined): string {
  if (value == null) return '—';
  const [whole, fraction = '00'] = value.split('.');
  const sign = whole.startsWith('-') ? '-' : '';
  const digits = whole.replace('-', '');
  const head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  const grouped = head
    ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`
    : tail;
  return `${sign}₹${grouped}.${fraction.padEnd(2, '0').slice(0, 2)}`;
}

/**
 * A whole-rupee figure, grouped the Indian way and with no paise.
 *
 * For the places a rupee amount is a *choice* rather than an account: a salary
 * band in a dropdown, a figure on a badge. "₹15,000.00" in a list of thirty
 * options is four characters of noise per row, and every one of these values
 * is a round thousand by construction.
 *
 * Takes a number, unlike money() above, which deliberately never parses the
 * decimal strings the API sends. These figures are the form's own — generated
 * from a step, not read from a column — so there is no precision to lose.
 */
export function rupeesWhole(value: number): string {
  const digits = String(Math.round(Math.abs(value)));
  const head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  const grouped = head
    ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`
    : tail;
  return `${value < 0 ? '-' : ''}₹${grouped}`;
}

/**
 * A salary as whole rupees, grouped the Indian way: `40000.00` -> `40,000`.
 *
 * The candidate columns printed the column verbatim -- `40000.00` -- so every
 * row carried two zeroes that are never anything else. A salary is agreed in
 * thousands; the paise are an artefact of the Numeric type, not a figure
 * anybody stated.
 *
 * Formats the string rather than parsing it, like `money()` above and for the
 * same reason: a rupee amount must not round-trip through a float on its way
 * to the screen. It differs from `rupeesWhole` in taking the API's decimal
 * string instead of a number the form generated.
 *
 * No symbol. These sit under headings that already say Salary, and a column of
 * bare grouped figures stays a column of numbers rather than becoming a
 * currency statement.
 */
export function salaryWhole(value: string | null | undefined): string | null {
  if (value == null) return null;
  const whole = value.split('.')[0];
  const sign = whole.startsWith('-') ? '-' : '';
  const digits = whole.replace('-', '');
  const head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  const grouped = head
    ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`
    : tail;
  return `${sign}${grouped}`;
}
